import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { applyEvent } from "@/stores/sessionStore.events";
import {
  emptyArtifacts,
  emptyConnection,
  newId,
  type ChatMessage,
  type ChatRole,
  type SessionConnection,
  type SessionState,
  type SessionTier,
  type ToolActivity,
} from "@/stores/sessionStore.types";

// Re-export the public types so existing imports keep working.
export type { ChatMessage, ChatRole, SessionConnection, SessionState, SessionTier, ToolActivity };

// Persisted to **sessionStorage**, but `main.tsx` wipes the storage
// before zustand hydrates UNLESS the page just landed on a Stripe
// return path (`/checkout/success` or `/checkout/cancel`).
//
// Reducer logic lives in `sessionStore.events.ts` (LoopEvent →
// state slice) and `sessionStore.artifacts.ts` (tool result →
// ArtifactState). This file just wires the store + persist
// middleware (docs/plans/13 §M3 — 400-LOC ceiling).
export const useSessionStore = create<SessionState>()(
  persist(
    (set) => ({
      connection: emptyConnection,
      messages: [],
      tools: [],
      artifacts: emptyArtifacts,
      pendingTurn: false,

      setConnection: (patch) => set((s) => ({ connection: { ...s.connection, ...patch } })),
      setSession: ({
        sessionId,
        tier,
        expiresAt,
        stripeCustomerId,
        stripeCheckoutSessionId,
        recoveryAccessToken,
        hasPaymentEmail,
        medicalRecordStatus,
        letterPlan,
        weakPolicy,
        memberStatement,
        citationsVerified,
      }) =>
        set((s) => ({
          connection: {
            ...s.connection,
            sessionId,
            tier,
            expiresAt,
            hasPaymentEmail: hasPaymentEmail ?? s.connection.hasPaymentEmail,
            stripeCustomerId: stripeCustomerId ?? s.connection.stripeCustomerId,
            stripeCheckoutSessionId:
              stripeCheckoutSessionId ?? s.connection.stripeCheckoutSessionId,
            recoveryAccessToken: recoveryAccessToken ?? s.connection.recoveryAccessToken,
            medicalRecordStatus: medicalRecordStatus ?? s.connection.medicalRecordStatus,
            // `letterPlan` is passed explicitly (including null) on every
            // sync, so replace rather than coalesce — undefined means the
            // caller didn't touch it.
            letterPlan: letterPlan === undefined ? s.connection.letterPlan : letterPlan,
            weakPolicy: weakPolicy ?? s.connection.weakPolicy,
            memberStatement:
              memberStatement === undefined ? s.connection.memberStatement : memberStatement,
            citationsVerified: citationsVerified ?? s.connection.citationsVerified,
          },
        })),
      resetSession: () =>
        set({
          connection: emptyConnection,
          messages: [],
          tools: [],
          artifacts: emptyArtifacts,
          pendingTurn: false,
        }),
      appendMessage: (message) => set((s) => ({ messages: [...s.messages, message] })),
      setPendingTurn: (pending) => set({ pendingTurn: pending }),
      clearPendingTurn: (reason) =>
        set((s) => {
          if (!s.pendingTurn) return s;
          return {
            pendingTurn: false,
            messages: [
              ...s.messages,
              {
                id: newId(),
                role: "system",
                content: `Connection lost (${reason}). Your last message wasn't completed — try sending it again.`,
                timestamp: new Date().toISOString(),
              },
            ],
          };
        }),

      applyEvent: (event) => set((s) => applyEvent(s, event)),
    }),
    {
      name: "revetra.session",
      // v11: connection now carries recordImportanceAckVersion (the
      // pre-payment record acknowledgment); bump so an older persisted
      // blob is discarded cleanly.
      version: 11,
      storage: createJSONStorage(() => sessionStorage),
      partialize: (s) => ({
        connection: {
          sessionId: s.connection.sessionId,
          tier: s.connection.tier,
          expiresAt: s.connection.expiresAt,
          hasPaymentEmail: s.connection.hasPaymentEmail,
          stripeCustomerId: s.connection.stripeCustomerId,
          stripeCheckoutSessionId: s.connection.stripeCheckoutSessionId,
          recoveryAccessToken: s.connection.recoveryAccessToken,
          consentBundleVersion: s.connection.consentBundleVersion,
          recordImportanceAckVersion: s.connection.recordImportanceAckVersion,
          medicalRecordStatus: s.connection.medicalRecordStatus,
          letterPlan: s.connection.letterPlan,
          weakPolicy: s.connection.weakPolicy,
          memberStatement: s.connection.memberStatement,
          citationsVerified: s.connection.citationsVerified,
          // connected / reconnecting are runtime-only.
          connected: false,
          reconnecting: false,
        },
        messages: s.messages,
        tools: s.tools,
        artifacts: s.artifacts,
      }),
    },
  ),
);
